<script setup lang="ts">
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import {
    parseHostPort,
    useNativePrintingUi,
  } from '@/composition/printing/use-native-printing-ui';
  import { INativeWizardDevice } from '@/composition/printing/native-wizard-commit';
  import { IPrinter } from '@/interfaces/IPrinter';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { TNativeWizardConnection } from './NativeStepConnection.vue';

  interface INetworkCandidate {
    id: string;
    ip: string;
    port: number;
    name: string;
  }

  const props = defineProps<{
    connection: TNativeWizardConnection;
  }>();

  const emit = defineEmits<{
    (e: 'select', device: INativeWizardDevice): void;
  }>();

  const ui = useNativePrintingUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  // #region USB / Bluetooth (discovered list)

  const refreshing = ref(false);

  /**
   * INTERACTIVE on purpose (unlike tab mounts): the user just tapped the
   * USB/Bluetooth connection card, so permission/enable prompts are
   * legitimately user-initiated here — and the interactive reload merges
   * results into the store, so the finished printer's card won't render as
   * a ghost.
   */
  async function refresh() {
    if (refreshing.value) return;
    refreshing.value = true;
    try {
      await ui.reload(true);
    } finally {
      refreshing.value = false;
    }
  }

  const discoveredDevices = computed<IPrinter[]>(() =>
    (ui.printing.printers.value ?? []).filter(
      (p) => p.type === (props.connection === 'usb' ? 'usb' : 'bluetooth')
    )
  );

  const requestingId = ref<IPrinter['id'] | null>(null);

  async function selectDiscovered(printer: IPrinter) {
    if (requestingId.value !== null) return;

    if (printer.type === 'usb') {
      requestingId.value = printer.id;
      try {
        // System-side no-op when already granted; the strategy's IPrinter
        // carries no permission state, so we always ask. A denial is not a
        // hard stop — the Test step will reveal it honestly.
        const strategy = ui.nativeStrategyOrNull();
        if (strategy) await strategy.requestUsbPermission(printer);
      } catch (e) {
        console.error('[NativePrinting] requestUsbPermission failed', e);
      } finally {
        requestingId.value = null;
      }
    }

    emit('select', {
      type: printer.type,
      id: printer.id,
      name: printer.name,
      isNew: false,
    });
  }

  // #endregion

  // #region network (scan + add by IP), all wizard-local until Finish

  const scanning = ref(false);
  const scanDone = ref(false);
  const candidates = ref<INetworkCandidate[]>([]);

  function savedAddresses(): Set<string> {
    return new Set(
      ui.networkPrinters.value.map((np) => `${np.host}:${np.port}`)
    );
  }

  async function runScan() {
    if (scanning.value) return;
    scanning.value = true;
    try {
      const devices = await ui.scanNetwork();
      const saved = savedAddresses();
      candidates.value = devices
        .filter((d) => !saved.has(`${d.ip}:${d.port}`))
        .map((d) => ({ id: d.id, ip: d.ip, port: d.port, name: d.name }));
      scanDone.value = true;
    } catch (e) {
      console.error('[NativePrinting] wizard network scan failed', e);
      scanDone.value = true;
    } finally {
      scanning.value = false;
    }
  }

  const manualName = ref('');
  const manualHost = ref('');
  const manualPort = ref('9100');
  const manualError = ref<string | null>(null);

  const probingKey = ref<string | null>(null);
  /** Probed-unreachable device awaiting the user's "continue anyway". */
  const pendingUnreachable = ref<INativeWizardDevice | null>(null);

  /**
   * Probes the candidate through the STRATEGY (serialized against jobs,
   * feeds the health map) and advances. The transient health-map entry the
   * probe writes for a not-yet-saved address is harmless — pruned on the
   * next sweep. Unreachable → inline warning but the user may proceed
   * (printer may simply be off; matches the previous add semantics).
   */
  async function probeAndSelect(rawName: string, host: string, port: number) {
    if (probingKey.value !== null) return;
    const address = `${host}:${port}`;

    if (savedAddresses().has(address)) {
      manualError.value = i18nT('printingV2.nativeAddressExists');
      return;
    }

    probingKey.value = address;
    pendingUnreachable.value = null;
    let reachable = false;
    let supportsDleEot = false;
    try {
      const strategy = ui.nativeStrategyOrNull();
      if (strategy) {
        const result = await strategy.probeNetworkPrinter(address);
        reachable = result.reachable;
        supportsDleEot = result.supportsDleEot;
      }
    } catch (e) {
      console.error('[NativePrinting] wizard probe failed', e);
    } finally {
      probingKey.value = null;
    }

    const device: INativeWizardDevice = {
      type: 'tcp',
      id: address,
      name: rawName.trim() || `Impresora de red (${host})`,
      host,
      port,
      isNew: true,
      reachable,
      supportsDleEot,
    };

    if (reachable) {
      emit('select', device);
    } else {
      pendingUnreachable.value = device;
    }
  }

  function selectCandidate(candidate: INetworkCandidate) {
    void probeAndSelect(candidate.name, candidate.ip, candidate.port);
  }

  function submitManual() {
    manualError.value = null;
    const parsed = parseHostPort(manualHost.value, manualPort.value);
    if (!parsed.ok) {
      manualError.value = i18nT(
        parsed.error === 'host'
          ? 'printingV2.nativeInvalidHost'
          : 'printingV2.nativeInvalidPort'
      );
      return;
    }
    void probeAndSelect(manualName.value, parsed.host, parsed.port);
  }

  function continueUnreachable() {
    const device = pendingUnreachable.value;
    if (!device) return;
    pendingUnreachable.value = null;
    emit('select', device);
  }

  // #endregion

  onMounted(() => {
    if (props.connection === 'network') {
      void runScan();
    } else {
      void refresh();
    }
  });
</script>

<template>
  <div class="native-step-device">
    <!-- USB / Bluetooth: discovered devices -->
    <template v-if="connection !== 'network'">
      <div class="device-toolbar">
        <div v-if="refreshing" class="device-searching">
          <span class="device-searching-spinner" />
          {{ $t('printingV2.searching') }}
        </div>
        <div v-else class="device-search-done">
          {{ $t('printingV2.searchFinished') }}
        </div>
        <PosUiButton
          color="neutral"
          variant="outline"
          size="sm"
          icon="refresh"
          :loading="refreshing"
          @click="refresh"
        >
          {{ $t('printingV2.refresh') }}
        </PosUiButton>
      </div>

      <div v-if="discoveredDevices.length" class="device-list">
        <button
          v-for="printer in discoveredDevices"
          :key="`${printer.type}:${printer.id}`"
          type="button"
          class="device-row"
          :disabled="requestingId !== null"
          @click="selectDiscovered(printer)"
        >
          <PosUiIcon
            :name="printer.type === 'usb' ? 'usb' : 'bluetooth'"
            class="device-row-icon"
          />
          <span class="device-row-name">
            {{ printer.name }}
            <span class="device-row-detail">{{ printer.id }}</span>
          </span>
          <span v-if="requestingId === printer.id" class="device-row-spinner" />
          <PosUiIcon v-else name="chevron-right" class="device-row-chevron" />
        </button>
      </div>
      <div v-else-if="!refreshing" class="device-empty">
        {{ $t('printingV2.noResultsFound') }}
      </div>

      <!-- Bluetooth pairing guide (Android wording) -->
      <div v-if="connection === 'bluetooth'" class="device-bt-guide">
        <div class="bt-guide-title">{{ $t('printingV2.btGuideTitle') }}</div>
        <ol class="bt-guide-steps">
          <li>{{ $t('printingV2.nativeBtGuide1') }}</li>
          <li>{{ $t('printingV2.nativeBtGuide2') }}</li>
          <li>{{ $t('printingV2.nativeBtGuide3') }}</li>
        </ol>
      </div>
    </template>

    <!-- Network: scan + add by IP -->
    <template v-else>
      <div class="device-toolbar">
        <div v-if="scanning" class="device-searching">
          <span class="device-searching-spinner" />
          {{ $t('printingV2.searching') }}
        </div>
        <div v-else-if="scanDone" class="device-search-done">
          {{ $t('printingV2.searchFinished') }}
        </div>
        <PosUiButton
          color="neutral"
          variant="outline"
          size="sm"
          icon="refresh"
          :loading="scanning"
          @click="runScan"
        >
          {{ $t('printingV2.refresh') }}
        </PosUiButton>
      </div>

      <div v-if="candidates.length" class="device-list">
        <button
          v-for="candidate in candidates"
          :key="candidate.id"
          type="button"
          class="device-row"
          :disabled="probingKey !== null"
          @click="selectCandidate(candidate)"
        >
          <PosUiIcon name="lan" class="device-row-icon" />
          <span class="device-row-name">
            {{ candidate.name }}
            <span class="device-row-detail">
              {{ candidate.ip }}:{{ candidate.port }}
            </span>
          </span>
          <span
            v-if="probingKey === `${candidate.ip}:${candidate.port}`"
            class="device-row-spinner"
          />
          <PosUiIcon v-else name="chevron-right" class="device-row-chevron" />
        </button>
      </div>
      <div v-else-if="scanDone && !scanning" class="device-empty">
        {{ $t('printingV2.noResultsFound') }}
      </div>

      <!-- Add by IP -->
      <div class="device-add-by-ip">
        <div class="add-by-ip-title">{{ $t('printingV2.addByIp') }}</div>
        <div class="add-by-ip-grid">
          <div class="add-by-ip-field">
            <label class="add-by-ip-label">{{
              $t('printingV2.nativeAddName')
            }}</label>
            <PosUiInput
              :value="manualName"
              size="sm"
              :maxlength="40"
              @input="manualName = $event"
            />
          </div>
          <div class="add-by-ip-field">
            <label class="add-by-ip-label">{{
              $t('printingV2.nativeAddHost')
            }}</label>
            <PosUiInput
              :value="manualHost"
              size="sm"
              placeholder="192.168.1.100"
              @input="
                manualHost = $event;
                manualError = null;
              "
              @enter="submitManual"
            />
          </div>
          <div class="add-by-ip-field add-by-ip-field-port">
            <label class="add-by-ip-label">{{
              $t('printingV2.nativeAddPort')
            }}</label>
            <PosUiInput
              :value="manualPort"
              size="sm"
              inputmode="numeric"
              :maxlength="5"
              @input="
                manualPort = $event;
                manualError = null;
              "
              @enter="submitManual"
            />
          </div>
          <PosUiButton
            color="primary"
            variant="soft"
            size="sm"
            class="add-by-ip-submit"
            :loading="probingKey !== null"
            :disabled="!manualHost.trim()"
            @click="submitManual"
          >
            {{ $t('printingV2.ipAdd') }}
          </PosUiButton>
        </div>
        <div v-if="manualError" class="add-by-ip-error">{{ manualError }}</div>
      </div>

      <!-- Probed unreachable: warn but allow proceeding -->
      <div v-if="pendingUnreachable" class="device-unreachable">
        <div class="device-unreachable-text">
          <PosUiIcon name="alert" class="device-unreachable-icon" />
          {{ $t('printingV2.nativeUnreachable') }}
        </div>
        <PosUiButton
          color="warning"
          variant="soft"
          size="sm"
          @click="continueUnreachable"
        >
          {{ $t('printingV2.nativeContinueAnyway') }}
        </PosUiButton>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .native-step-device {
    @apply tw-flex tw-flex-col tw-gap-3;

    .device-toolbar {
      @apply tw-flex tw-min-h-[36px] tw-items-center tw-justify-between tw-gap-2;

      .device-searching {
        @apply tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-neutral-500;

        .device-searching-spinner {
          @apply tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-neutral-300 tw-border-t-orange-500;
        }
      }

      .device-search-done {
        @apply tw-text-sm tw-text-neutral-500;
      }
    }

    .device-empty {
      @apply tw-rounded-lg tw-bg-neutral-50 tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-neutral-500;
    }

    .device-list {
      @apply tw-flex tw-max-h-[280px] tw-flex-col tw-gap-1.5 tw-overflow-y-auto;

      .device-row {
        @apply tw-flex tw-min-h-[52px] tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-px-3.5 tw-py-2 tw-text-left tw-transition-colors hover:tw-border-orange-300 hover:tw-bg-orange-50/40;

        &:disabled {
          @apply tw-cursor-not-allowed tw-opacity-60;
        }

        .device-row-icon {
          @apply tw-flex-none tw-text-xl tw-text-neutral-400;
        }

        .device-row-name {
          @apply tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-medium tw-text-neutral-800;

          .device-row-detail {
            @apply tw-ml-1.5 tw-text-xs tw-font-normal tw-text-neutral-400;
          }
        }

        .device-row-chevron {
          @apply tw-flex-none tw-text-lg tw-text-neutral-300;
        }

        .device-row-spinner {
          @apply tw-h-4 tw-w-4 tw-flex-none tw-animate-spin tw-rounded-full tw-border-2 tw-border-neutral-300 tw-border-t-orange-500;
        }
      }
    }

    .device-add-by-ip {
      @apply tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-p-3.5;

      .add-by-ip-title {
        @apply tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }

      .add-by-ip-grid {
        @apply tw-flex tw-flex-wrap tw-items-end tw-gap-2;

        .add-by-ip-field {
          @apply tw-flex tw-min-w-[140px] tw-flex-1 tw-flex-col tw-gap-1;

          &.add-by-ip-field-port {
            @apply tw-min-w-[80px] tw-max-w-[110px] tw-flex-none;
          }

          .add-by-ip-label {
            @apply tw-text-xs tw-font-medium tw-text-neutral-600;
          }
        }

        .add-by-ip-submit {
          @apply tw-flex-none;
        }
      }

      .add-by-ip-error {
        @apply tw-mt-1.5 tw-text-xs tw-text-red-600;
      }
    }

    .device-bt-guide {
      @apply tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-p-3.5;

      .bt-guide-title {
        @apply tw-mb-2 tw-text-sm tw-font-semibold tw-text-neutral-700;
      }

      .bt-guide-steps {
        @apply tw-m-0 tw-flex tw-flex-col tw-gap-1 tw-pl-5 tw-text-sm tw-text-neutral-600;
      }
    }

    .device-unreachable {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-4 tw-py-3;

      .device-unreachable-text {
        @apply tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-sm tw-text-amber-800;
      }

      .device-unreachable-icon {
        @apply tw-flex-none tw-text-base;
      }
    }
  }
</style>
